import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { BadgeCheck, Check, Plus, Save, Trash2 } from "lucide-react";
import BASE_URL from "../../config/config";
import Principal from "../Principal/Principal";
import { useToast } from "../Global/ToastContext";
import logoWhite from "../../imagenes/Balto_Blanco.png";
import logoMark from "../../imagenes/balto.png";

const ENDPOINTS = {
  load: "web_home_obtener",
  savePlans: "admin_planes_guardar",
};

function buildApiUrl(action, params = {}) {
  const base = BASE_URL.replace(/\/$/, "");
  const query = new URLSearchParams({ action, ...params });

  return `${base}/api.php?${query.toString()}`;
}

function normalizePlan(plan, index = 0) {
  let incluye = [];

  if (Array.isArray(plan?.incluye)) {
    incluye = plan.incluye;
  } else if (typeof plan?.incluye === "string") {
    incluye = plan.incluye
      .split("\n")
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return {
    id: plan?.id ?? `new-${Date.now()}-${index}`,
    nombre: plan?.nombre ?? "",
    precio: plan?.precio ?? "",
    incluye,
    orden: Number(plan?.orden ?? index + 1),
  };
}

function getAuthHeaders() {
  return {
    "Content-Type": "application/json",
    "X-Requested-With": "XMLHttpRequest",
  };
}

function Boton({ children, onClick, disabled = false, variant = "primary", className = "" }) {
  const base =
    "inline-flex items-center justify-center rounded-2xl px-4 py-2.5 text-sm font-medium transition duration-200 disabled:cursor-not-allowed disabled:opacity-60";

  const styles =
    variant === "secondary"
      ? "border border-[var(--balto-border,#d8e0ec)] bg-white/80 text-slate-700 shadow-[0_10px_30px_rgba(15,23,42,0.05)] hover:-translate-y-0.5 hover:bg-white"
      : variant === "danger"
      ? "border border-red-100 bg-red-50 text-red-700 hover:-translate-y-0.5 hover:bg-red-100"
      : "bg-[var(--balto-action,#0055BB)] text-white shadow-[0_14px_30px_rgba(0,85,187,0.22)] hover:-translate-y-0.5 hover:opacity-95";

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`${base} ${styles} ${className}`}
    >
      {children}
    </button>
  );
}

function Campo({ label, value, onChange, placeholder = "", type = "text" }) {
  return (
    <label className="balto-floating-label block">
      <span>{label}</span>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full rounded-2xl border border-[var(--balto-border,#d8e0ec)] bg-white/90 px-4 pb-3 text-sm font-normal text-slate-800 shadow-[0_10px_28px_rgba(15,23,42,0.04)] outline-none transition duration-200 placeholder:text-slate-300 focus:border-[var(--balto-action,#0055BB)] focus:bg-white focus:ring-4 focus:ring-[var(--balto-action,#0055BB)]/10"
      />
    </label>
  );
}

function TarjetaSeccion({ title, subtitle, children, actions }) {
  return (
    <section className="balto-soft-card relative overflow-hidden rounded-[30px]">
      <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(0,85,187,0.08),transparent_30%)]" />
      <img
        src={logoMark}
        alt=""
        aria-hidden="true"
        className="pointer-events-none absolute -right-12 -top-16 h-48 w-48 object-contain opacity-[0.035]"
      />

      <div className="relative border-b border-[var(--balto-border,#d8e0ec)]/70 px-6 py-5 sm:px-7">
        <span className="inline-flex items-center rounded-full border border-[var(--balto-border,#d8e0ec)] bg-white/70 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.16em] text-[var(--balto-action,#0055BB)]">
          Balto Admin
        </span>

        <h2 className="mt-3 text-2xl font-semibold tracking-[-0.02em] text-[var(--balto-midnight,#0a2540)]">
          {title}
        </h2>

        {subtitle && (
          <p className="mt-2 max-w-2xl text-sm font-normal leading-6 text-[var(--balto-muted,#5b6472)]">
            {subtitle}
          </p>
        )}
      </div>

      <div className="relative px-5 py-6 sm:px-7">{children}</div>

      {actions && (
        <div className="relative flex flex-col gap-3 border-t border-[var(--balto-border,#d8e0ec)]/70 bg-white/45 px-5 py-4 sm:flex-row sm:justify-end sm:px-7">
          {actions}
        </div>
      )}
    </section>
  );
}

function PlanPreview({ plan, index }) {
  const featured = index === 1 || /pro|premium|empresa/i.test(plan?.nombre || "");
  const incluye = Array.isArray(plan?.incluye)
    ? plan.incluye.filter((item) => String(item ?? "").trim())
    : [];

  return (
    <article
      className={`relative flex h-full flex-col overflow-hidden rounded-[28px] border p-6 transition duration-300 hover:-translate-y-1 ${
        featured
          ? "border-[var(--balto-action)]/50 bg-gradient-to-b from-[rgba(0,85,187,0.28)] via-[rgba(7,18,36,0.96)] to-[rgba(3,8,18,1)] shadow-[0_0_0_1px_rgba(0,85,187,0.10),0_30px_80px_rgba(0,85,187,0.20)]"
          : "border-white/10 bg-white/[0.045] shadow-[0_20px_60px_rgba(0,0,0,0.22)] backdrop-blur-xl"
      }`}
    >
      {featured && (
        <div className="absolute left-1/2 top-4 -translate-x-1/2">
          <span className="rounded-full border border-white/10 bg-white/10 px-3 py-1 text-[10px] font-medium uppercase tracking-[0.18em] text-white backdrop-blur-md">
            Recomendado
          </span>
        </div>
      )}

      <div className="pointer-events-none absolute inset-0 rounded-[28px] bg-[linear-gradient(180deg,rgba(255,255,255,0.08),transparent_25%,transparent_78%,rgba(255,255,255,0.03))]" />

      <div className="relative z-10 flex h-full flex-col pt-4">
        <span
          className={`inline-flex w-fit items-center rounded-full px-3 py-1 text-[11px] font-medium uppercase tracking-[0.16em] ${
            featured
              ? "bg-white/10 text-white"
              : "bg-[var(--balto-action)]/10 text-[var(--balto-action)]"
          }`}
        >
          Plan {index + 1}
        </span>

        <h3 className="mt-5 text-2xl font-semibold tracking-tight text-white">
          {plan?.nombre || "Sin nombre"}
        </h3>

        <div className="mt-6 rounded-[24px] border border-white/10 bg-black/20 p-5">
          <div className="text-3xl font-semibold tracking-tight text-white">
            {plan?.precio || "Precio"}
          </div>
          <p className="mt-2 text-sm text-white/[0.45]">Vista previa del plan.</p>
        </div>

        <ul className="mt-6 space-y-3">
          {incluye.length > 0 ? (
            incluye.slice(0, 5).map((item, itemIndex) => (
              <li key={`${item}-${itemIndex}`} className="flex gap-3 text-sm leading-6 text-white/70">
                <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[var(--balto-action)]/20 text-[var(--balto-action)]">
                  <Check size={13} />
                </span>
                <span>{item}</span>
              </li>
            ))
          ) : (
            <li className="text-sm leading-6 text-white/[0.45]">Sin ítems cargados.</li>
          )}
        </ul>
      </div>
    </article>
  );
}

export default function PanelWeb() {
  const { section } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [plans, setPlans] = useState([]);
  const [savingPlans, setSavingPlans] = useState(false);

  const loadUrl = useMemo(() => buildApiUrl(ENDPOINTS.load), []);

  useEffect(() => {
    if (!section || section !== "planes") {
      navigate("/admin/landing/planes", { replace: true });
    }
  }, [section, navigate]);

  useEffect(() => {
    let active = true;

    const fetchData = async () => {
      try {
        setLoading(true);

        const res = await fetch(loadUrl, { credentials: "include" });
        const json = await res.json();
        const data = json?.data ?? json ?? {};
        const planesBackend = data.plans ?? data.planes ?? [];

        if (!active) return;

        setPlans(
          Array.isArray(planesBackend)
            ? planesBackend.map((item, index) => normalizePlan(item, index))
            : []
        );
      } catch (error) {
        showToast("No se pudieron cargar los planes.", "error");
      } finally {
        if (active) setLoading(false);
      }
    };

    fetchData();

    return () => {
      active = false;
    };
  }, [loadUrl, showToast]);

  const updatePlan = (index, field, value) => {
    setPlans((prev) =>
      prev.map((item, i) => (i === index ? { ...item, [field]: value } : item))
    );
  };

  const updatePlanIncluye = (planIndex, incluyeIndex, value) => {
    setPlans((prev) =>
      prev.map((plan, i) => {
        if (i !== planIndex) return plan;

        const nuevoIncluye = Array.isArray(plan.incluye) ? [...plan.incluye] : [];
        nuevoIncluye[incluyeIndex] = value;

        return { ...plan, incluye: nuevoIncluye };
      })
    );
  };

  const addPlanIncluye = (planIndex) => {
    setPlans((prev) =>
      prev.map((plan, i) => {
        if (i !== planIndex) return plan;

        return {
          ...plan,
          incluye: [...(Array.isArray(plan.incluye) ? plan.incluye : []), ""],
        };
      })
    );
  };

  const removePlanIncluye = (planIndex, incluyeIndex) => {
    setPlans((prev) =>
      prev.map((plan, i) => {
        if (i !== planIndex) return plan;

        return {
          ...plan,
          incluye: (Array.isArray(plan.incluye) ? plan.incluye : []).filter(
            (_, idx) => idx !== incluyeIndex
          ),
        };
      })
    );
  };

  const addPlan = () => {
    setPlans((prev) => [
      ...prev,
      {
        id: `new-${Date.now()}`,
        nombre: "",
        precio: "",
        incluye: [],
        orden: prev.length + 1,
      },
    ]);
  };

  const removePlan = (index) => {
    setPlans((prev) => prev.filter((_, i) => i !== index));
  };

  const savePlans = async () => {
    try {
      setSavingPlans(true);

      const payload = {
        items: plans.map((item, index) => ({
          id: typeof item.id === "number" ? item.id : undefined,
          nombre: String(item.nombre ?? "").trim(),
          precio: String(item.precio ?? "").trim(),
          incluye: Array.isArray(item.incluye)
            ? item.incluye
                .map((v) => String(v ?? "").trim())
                .filter(Boolean)
                .join("\n")
            : "",
          orden: Number(item.orden ?? index + 1),
        })),
      };

      const res = await fetch(buildApiUrl(ENDPOINTS.savePlans), {
        method: "POST",
        headers: getAuthHeaders(),
        credentials: "include",
        body: JSON.stringify(payload),
      });

      const json = await res.json();

      if (!json?.exito) {
        throw new Error(
          json?.error || json?.mensaje || "No se pudieron guardar los planes."
        );
      }

      const data = json?.data ?? [];
      setPlans(Array.isArray(data) ? data.map((item, index) => normalizePlan(item, index)) : plans);
      showToast("Planes guardados correctamente.", "exito");
    } catch (error) {
      showToast(error.message || "Error al guardar planes.", "error");
    } finally {
      setSavingPlans(false);
    }
  };

  if (loading) {
    return (
      <Principal>
        <div className="space-y-4">
          <div className="balto-glass-card h-40 animate-pulse rounded-[30px]" />
          <div className="balto-soft-card h-52 animate-pulse rounded-[30px]" />
          <div className="balto-soft-card h-52 animate-pulse rounded-[30px]" />
        </div>
      </Principal>
    );
  }

  return (
    <Principal>
      <div className="space-y-6">
        <section className="balto-glass-card balto-animate-up relative overflow-hidden rounded-[32px] px-6 py-7 sm:px-8">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(0,85,187,0.10),transparent_34%)]" />
          <img
            src={logoMark}
            alt=""
            aria-hidden="true"
            className="pointer-events-none absolute -right-10 -top-12 h-52 w-52 object-contain opacity-[0.035]"
          />

          <div className="relative max-w-3xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-[var(--balto-border)] bg-white/80 px-4 py-2 text-sm font-medium text-[var(--balto-action)] shadow-sm backdrop-blur-md">
              <BadgeCheck size={16} />
              Balto Web
            </span>

            <h1 className="mt-5 text-4xl font-semibold tracking-[-0.035em] text-[var(--balto-midnight)] lg:text-5xl">
              Editor de Planes
            </h1>
            <p className="mt-4 max-w-2xl text-base leading-8 text-[var(--balto-muted)]">
              Las demás secciones de la web quedaron fijas. Desde acá solamente modificás los planes que se muestran desde la base de datos.
            </p>
          </div>
        </section>

        <TarjetaSeccion
          title="Planes"
          subtitle="Cargá nombre, precio, orden e ítems incluidos. El estilo replica las tarjetas de pricing del frontend público."
          actions={
            <div className="flex flex-col gap-3 sm:flex-row">
              <Boton variant="secondary" onClick={addPlan}>
                <span className="inline-flex items-center gap-2">
                  <Plus size={16} />
                  Agregar plan
                </span>
              </Boton>

              <Boton onClick={savePlans} disabled={savingPlans}>
                <span className="inline-flex items-center gap-2">
                  <Save size={16} />
                  {savingPlans ? "Guardando..." : "Guardar planes"}
                </span>
              </Boton>
            </div>
          }
        >
          <div className="space-y-5">
            {plans.map((item, index) => (
              <article
                key={item.id ?? index}
                className="group relative overflow-hidden rounded-[28px] border border-[var(--balto-border,#d8e0ec)]/80 bg-white/80 p-4 shadow-[0_12px_35px_rgba(15,23,42,0.05)] transition duration-300 hover:-translate-y-0.5 hover:bg-white/90 sm:p-5"
              >
                <img
                  src={logoMark}
                  alt=""
                  aria-hidden="true"
                  className="pointer-events-none absolute -right-8 -top-12 h-36 w-36 object-contain opacity-[0.035] transition duration-300 group-hover:opacity-[0.055]"
                />

                <div className="relative mb-5 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                  <div>
                    <span className="inline-flex items-center rounded-full bg-[var(--balto-action)]/10 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.16em] text-[var(--balto-action)]">
                      Plan #{index + 1}
                    </span>
                    <h3 className="mt-2 text-lg font-semibold tracking-tight text-[var(--balto-midnight,#0a2540)]">
                      {item.nombre || "Nuevo plan"}
                    </h3>
                  </div>

                  <Boton variant="danger" onClick={() => removePlan(index)}>
                    <span className="inline-flex items-center gap-2">
                      <Trash2 size={15} />
                      Eliminar
                    </span>
                  </Boton>
                </div>

                <div className="relative grid grid-cols-1 gap-4 md:grid-cols-3">
                  <Campo
                    label="Nombre"
                    value={item.nombre ?? ""}
                    onChange={(value) => updatePlan(index, "nombre", value)}
                    placeholder="Ej: Profesional"
                  />

                  <Campo
                    label="Precio"
                    value={item.precio ?? ""}
                    onChange={(value) => updatePlan(index, "precio", value)}
                    placeholder="Ej: $24.900/mes"
                  />

                  <Campo
                    label="Orden"
                    value={item.orden ?? index + 1}
                    onChange={(value) => updatePlan(index, "orden", value)}
                    placeholder="Ej: 2"
                  />

                  <div className="rounded-[24px] border border-[var(--balto-border,#d8e0ec)] bg-white/80 p-4 shadow-[0_10px_28px_rgba(15,23,42,0.04)] md:col-span-3">
                    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <h4 className="text-sm font-semibold tracking-tight text-[var(--balto-midnight,#0a2540)]">
                          Incluye
                        </h4>
                        <p className="mt-1 text-xs leading-5 text-[var(--balto-muted,#5b6472)]">
                          Agregá los ítems que se mostrarán debajo del precio.
                        </p>
                      </div>

                      <Boton variant="secondary" onClick={() => addPlanIncluye(index)}>
                        <span className="inline-flex items-center gap-2">
                          <Plus size={15} />
                          Agregar ítem
                        </span>
                      </Boton>
                    </div>

                    <div className="space-y-3">
                      {(item.incluye ?? []).map((inc, incIndex) => (
                        <div
                          key={incIndex}
                          className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto]"
                        >
                          <Campo
                            label={`Ítem #${incIndex + 1}`}
                            value={inc ?? ""}
                            onChange={(value) =>
                              updatePlanIncluye(index, incIndex, value)
                            }
                            placeholder="Ej: Reportes mensuales"
                          />

                          <div className="flex items-end">
                            <Boton
                              variant="secondary"
                              onClick={() => removePlanIncluye(index, incIndex)}
                              className="w-full md:w-auto"
                            >
                              Eliminar
                            </Boton>
                          </div>
                        </div>
                      ))}

                      {(item.incluye ?? []).length === 0 && (
                        <div className="rounded-2xl border border-dashed border-[var(--balto-border,#d8e0ec)] bg-slate-50/80 p-4 text-sm text-[var(--balto-muted,#5b6472)]">
                          No hay ítems cargados para este plan.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </article>
            ))}

            {plans.length === 0 && (
              <div className="rounded-[24px] border border-dashed border-[var(--balto-border,#d8e0ec)] bg-white/70 p-6 text-center">
                <p className="text-sm text-[var(--balto-muted,#5b6472)]">
                  No hay planes cargados todavía.
                </p>
              </div>
            )}
          </div>
        </TarjetaSeccion>

        <section className="relative overflow-hidden rounded-[32px] bg-[#04070d] p-6 text-white shadow-[0_24px_70px_rgba(0,0,0,0.30)] sm:p-8">
          <div className="pointer-events-none absolute inset-0">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top,rgba(0,85,187,0.18),transparent_32%)]" />
            <div className="absolute bottom-0 left-0 h-64 w-64 rounded-full bg-[var(--balto-action)]/10 blur-[120px]" />
            <div className="absolute inset-0 opacity-[0.04] [background-image:radial-gradient(#ffffff_1px,transparent_1px)] [background-size:22px_22px]" />
            <img
              src={logoWhite}
              alt=""
              aria-hidden="true"
              className="absolute -right-16 top-8 hidden h-80 w-80 object-contain opacity-[0.025] lg:block"
            />
          </div>

          <div className="relative">
            <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/5 px-4 py-2 text-xs font-medium uppercase tracking-[0.18em] text-white/70 backdrop-blur-md">
              Vista previa
            </span>
            <h2 className="mt-4 text-3xl font-semibold tracking-tight text-white">
              Así se sienten los planes en la web pública
            </h2>
            <p className="mt-3 max-w-2xl text-sm leading-7 text-white/60">
              Esta previsualización mantiene el lenguaje visual oscuro del pricing original de Balto.
            </p>

            <div className="mt-8 grid gap-5 lg:grid-cols-3">
              {plans.length > 0 ? (
                plans.slice(0, 3).map((plan, index) => (
                  <PlanPreview key={plan.id ?? index} plan={plan} index={index} />
                ))
              ) : (
                <div className="rounded-[24px] border border-white/10 bg-white/[0.045] p-6 text-sm text-white/50 lg:col-span-3">
                  Agregá planes para ver la previsualización.
                </div>
              )}
            </div>
          </div>
        </section>
      </div>
    </Principal>
  );
}
